import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../components/contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EntityModal from '../components/common/EntityModal';
import { useToast } from '../components/contexts/ToastContext';
import Breadcrumb from '../components/common/Breadcrumb';
import apiService from '../services/apiService';
import ROUTES from '../constants/routes';

/**
 * Página de gestión de roles del sistema (solo ADMIN)
 *
 * @returns {JSX.Element}
 */
function GestionRoles() {
  const { hasPermission } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRol, setEditingRol] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, rolId: null, rolNombre: '' });
  const { showToast } = useToast();

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getRoles();
      if (data.success) {
        setRoles(data.data);
      }
    } catch {
      showToast('Error al cargar roles', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const handleCreate = () => {
    setEditingRol(null);
    setModalOpen(true);
  };

  const handleEdit = (rol) => {
    setEditingRol(rol);
    setModalOpen(true);
  };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      let result;
      if (editingRol) {
        result = await apiService.updateRol(editingRol.id, formData);
      } else {
        result = await apiService.createRol(formData);
      }
      if (result.success) {
        showToast(result.message || (editingRol ? 'Rol actualizado' : 'Rol creado'), 'success');
        setModalOpen(false);
        fetchRoles();
      } else {
        showToast(result.message || 'Error al guardar', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (rol) => {
    setConfirmDialog({ open: true, rolId: rol.id, rolNombre: rol.nombre });
  };

  const confirmDelete = async () => {
    try {
      const result = await apiService.deleteRol(confirmDialog.rolId);
      if (result.success) {
        showToast('Rol eliminado exitosamente', 'success');
        fetchRoles();
      } else {
        showToast(result.message || 'Error al eliminar', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Error al eliminar', 'error');
    } finally {
      setConfirmDialog({ open: false, rolId: null, rolNombre: '' });
    }
  };

  const modalFields = [
    { name: 'codigo', label: 'Código', type: 'text', required: true, maxLength: 20,
      placeholder: 'Ej: SUPERVISOR (mayúsculas, sin espacios)' },
    { name: 'nombre', label: 'Nombre', type: 'text', required: true, maxLength: 50 },
    { name: 'descripcion', label: 'Descripción', type: 'textarea' }
  ];

  // El código no es editable en modo edición
  const editFields = editingRol
    ? modalFields.filter(f => f.name !== 'codigo').concat([
        { name: 'activo', label: 'Activo', type: 'checkbox' }
      ])
    : modalFields;

  const SYSTEM_ROLES = ['ADMIN', 'BASICO'];

  return (
    <>
      <Navbar />
      <div className="main-content">
        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: 'Usuarios', to: ROUTES.GESTION_USUARIOS },
          { label: 'Roles' }
        ]}>
          {hasPermission('roles.assign_permissions') && (
            <Link to={ROUTES.ASIGNACION_PERMISOS} className="btn btn-secondary">
              <i className="fas fa-key"></i> Asignar Permisos
            </Link>
          )}
          {hasPermission('roles.create') && (
            <button className="btn btn-primary" onClick={handleCreate}>
              <i className="fas fa-plus"></i> Nuevo Rol
            </button>
          )}
        </Breadcrumb>

        {loading ? (
          <div className="loading-container">
            <i className="fas fa-spinner fa-spin"></i> Cargando roles...
          </div>
        ) : (
          <div className="table-container">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th>Descripción</th>
                  <th>Estado</th>
                  <th>Usuarios</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {roles.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted">No hay roles registrados</td>
                  </tr>
                ) : (
                  roles.map(rol => (
                    <tr key={rol.id}>
                      <td><code>{rol.codigo}</code></td>
                      <td>{rol.nombre}</td>
                      <td className="text-muted">{rol.descripcion || '—'}</td>
                      <td>
                        <span className={`badge ${rol.activo ? 'badge-success' : 'badge-warning'}`}>
                          {rol.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td>{rol.usuarios_count ?? 0}</td>
                      <td className="text-center">
                        <div className="action-buttons">
                          {hasPermission('roles.update') && (
                            <button
                              className="btn btn-sm btn-ghost-primary"
                              onClick={() => handleEdit(rol)}
                              title="Editar"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                          )}
                          {!SYSTEM_ROLES.includes(rol.codigo) && hasPermission('roles.delete') && (
                            <button
                              className="btn btn-sm btn-ghost-danger"
                              onClick={() => handleDelete(rol)}
                              title="Eliminar"
                              disabled={rol.usuarios_count > 0}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <EntityModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        title={editingRol ? `Editar Rol: ${editingRol.nombre}` : 'Nuevo Rol'}
        fields={editFields}
        initialData={editingRol}
        loading={saving}
      />

      <ConfirmDialog
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, rolId: null, rolNombre: '' })}
        onConfirm={confirmDelete}
        title="Eliminar Rol"
        message={`¿Estás seguro de que deseas eliminar el rol "${confirmDialog.rolNombre}"?`}
      />
    </>
  );
}

export default GestionRoles;
